import type * as React from "react";
import { type ReactNode, useState } from "react";
import { Link } from "./Link";
import { useTranslation } from "react-i18next";
import { styled } from "@mui/material";

type DivProps = JSX.IntrinsicElements["div"];
interface LongLegalTextProps extends DivProps {
  shortText: ReactNode;
  fullText: ReactNode;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
}

/**
 * Component that initially shows a short text and a see more link.
 * After clicking, changes to rendering the full text.
 *
 *
 * Text is contained in a div component which receives all other props
 *
 * @param shortText - Short text - can be any react node
 * @param fullText - Full text - can be any react node
 * @param disabled - disables the "see more" link
 * @param rest - <div> tag props
 */
export const SeeMoreLongText = ({ shortText, fullText, disabled, required, error, ...rest }: LongLegalTextProps) => {
  const { t } = useTranslation();

  const [moreVisible, setMoreVisible] = useState(false);

  const moreClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setMoreVisible(true);
  };

  return (
    <div {...rest}>
      {!moreVisible ? shortText : fullText}
      {required && <Asterisk error={error}>&thinsp;{"*"}</Asterisk>}
      {!moreVisible && (
        <>
          {" "}
          <Link className={"d-inline"} onClick={moreClick} disabled={disabled}>
            {t("newsletter.consent.seemore")}
          </Link>
        </>
      )}
    </div>
  );
};

const Asterisk = styled("span")<{ error?: boolean }>(({ error, theme }) => ({
  color: error ? theme.palette.error.main : undefined,
}));
